import {Text} from '@dropbox/dig-components/typography';
import {Split, Stack} from '@dropbox/dig-foundations';
import {TeamLine} from '@dropbox/dig-icons/assets';
import type {AiSearchAnswerStartedBy} from 'analytics/aiSearchAnalytics';
import {Employee} from 'client';
import {AvatarIcon} from 'components/DSYS/Avatar';
import {Title} from 'components/DSYS/Title';
import {t} from 'i18next';

import {LiveAnswer} from './LiveAnswer';
import {MeetingDetail} from './MeetingViews';
import {AskLine, PersonPreview} from './PersonPreview';
import {KnowledgePreview, NewsPreview} from './ResultPreviews';
import {SearchRow} from './searchRows';
import {peopleCount} from './TeamCards';
import {RowList} from './Typography';
import {personRow} from './useResultRows';

/** A question in the panel: the question as the view's one bold line, then its live answer. */
const AskPreview = ({
  question,
  startedBy,
}: {
  question: string;
  startedBy: AiSearchAnswerStartedBy;
}) => (
  <Stack gap="16">
    <Title size={18}>{question}</Title>
    <LiveAnswer question={question} label={t('ai_search_ask')} startedBy={startedBy} />
  </Stack>
);

/** The right-hand pane: whatever the highlighted row is, a little more of it. */
export const RowPreview = ({
  row,
  onOpenPerson,
  onSelect,
  onGo,
  embedded = false,
  startedBy = 'click',
}: {
  row?: SearchRow;
  onOpenPerson: (person: Employee) => void;
  /** Open something in the panel itself (a person chip, a meeting attendee). */
  onSelect?: (row: SearchRow) => void;
  /** Go to the row itself (open the article). */
  onGo: (row: SearchRow) => void;
  /** The title is already on screen (the question in the field): skip it. */
  embedded?: boolean;
  /** For a question: whether the box put it here on its own while typing, or it was picked. */
  startedBy?: AiSearchAnswerStartedBy;
}) => {
  if (!row) {
    return null;
  }
  const selectPerson = onSelect ? (person: Employee) => onSelect(personRow(person)) : undefined;
  switch (row.kind) {
    case 'person':
      return (
        <PersonPreview
          person={row.person}
          onOpen={onOpenPerson}
          onSelect={selectPerson}
          hideHeader={embedded}
          onSelectRow={onSelect}
        />
      );
    case 'ask':
    case 'askSuggestion':
      return embedded ? (
        <LiveAnswer question={row.question} label={t('ai_search_ask')} startedBy={startedBy} />
      ) : (
        <AskPreview question={row.question} startedBy={startedBy} />
      );
    case 'team':
      return (
        <Stack gap="12">
          <Split gap="12" alignY="center">
            <Split.Item>
              <AvatarIcon src={TeamLine} size="large" />
            </Split.Item>
            <Split.Item width="fill">
              <Title size={18}>{row.team.name}</Title>
              {row.team.members_count != null && (
                <Text size="small" color="faint">
                  {peopleCount(row.team.members_count)}
                </Text>
              )}
            </Split.Item>
          </Split>
          {row.team.description && <Text color="subtle">{row.team.description}</Text>}
          <RowList>
            <AskLine question={t('search_box_ask_team', {team: row.team.name})} />
          </RowList>
        </Stack>
      );
    case 'news':
      return <NewsPreview post={row.post} onOpen={() => onGo(row)} />;
    case 'knowledge':
      return <KnowledgePreview result={row.result} onOpen={() => onGo(row)} />;
    case 'meeting':
      return (
        <MeetingDetail
          meeting={row.meeting}
          onSelect={selectPerson ?? onOpenPerson}
          hideTitle={embedded}
        />
      );
    case 'workstream':
      return (
        <Stack gap="12">
          <Title size={18}>{row.workstream.name}</Title>
          <RowList>
            <AskLine question={t('search_box_ask_workstream', {workstream: row.workstream.name})} />
          </RowList>
        </Stack>
      );
  }
};
